/**
 * Pays de géocodage / autocomplétion adresse selon la locale UI.
 * Aligné sur les langues booking (fr, en, es, de, it, nl-BE, nl-NL).
 * `nl-BE` (Flandre) → géo `be` ; `nl-NL` (Pays-Bas) → géo `nl`.
 * Les landings SEO villes (Paris, Lyon…) restent GEO_COUNTRY_SEO_CITIES.
 */

import type { BookingLanguageCode } from './i18n';
import { DUTCH_LOCALE_META, isDutchRegionalCode } from './dutchLocale';

/** ISO 3166-1 alpha-2 — Google Places `restrictions.country`, Nominatim `countrycodes` */
export const GEO_COUNTRY_BY_LOCALE: Record<BookingLanguageCode, string> = {
  fr: 'fr',
  en: 'gb',
  es: 'es',
  de: 'de',
  it: 'it',
  'nl-BE': DUTCH_LOCALE_META['nl-BE'].geoCountry,
  'nl-NL': DUTCH_LOCALE_META['nl-NL'].geoCountry,
};

/** Villes hardcodées des landings métiers = toujours en France */
export const GEO_COUNTRY_SEO_CITIES = 'fr';

export function getGeoCountryForLocale(locale: BookingLanguageCode | string): string {
  if (isDutchRegionalCode(locale)) {
    return DUTCH_LOCALE_META[locale].geoCountry;
  }
  if (locale === 'nl') return DUTCH_LOCALE_META['nl-BE'].geoCountry;
  return GEO_COUNTRY_BY_LOCALE[locale as BookingLanguageCode] ?? 'fr';
}

/** Pays supportés pour autocomplétion / géocodage (marchés booking). */
export const BOOKING_GEO_COUNTRIES = [
  { code: 'fr', flag: '🇫🇷' },
  { code: 'be', flag: '🇧🇪' },
  { code: 'nl', flag: '🇳🇱' },
  { code: 'gb', flag: '🇬🇧' },
  { code: 'es', flag: '🇪🇸' },
  { code: 'de', flag: '🇩🇪' },
  { code: 'it', flag: '🇮🇹' },
] as const;

export type BookingGeoCountryCode = (typeof BOOKING_GEO_COUNTRIES)[number]['code'];

/**
 * Pays ouverts pour la recherche Google « Où » (FR + voisins EU).
 * Legacy Places Autocomplete : max 5 codes — voir `placesSearchCountryRestriction`.
 */
export const BOOKING_PLACES_SEARCH_COUNTRIES = [
  'fr',
  'be',
  'nl',
  'es',
  'de',
  'it',
] as const;

/** Limite Google Places Autocomplete (legacy) pour `componentRestrictions.country`. */
export const PLACES_AUTOCOMPLETE_MAX_COUNTRIES = 5;

/**
 * Restriction multi-pays pour Places : pays courant en premier, puis les autres marchés EU.
 * Tronque à 5 (contrainte API legacy). Ex. geo=fr → fr,be,nl,es,de.
 */
export function placesSearchCountryRestriction(
  preferredCountry?: string | null
): string[] {
  const preferred = (preferredCountry || 'fr').trim().toLowerCase();
  const base = BOOKING_PLACES_SEARCH_COUNTRIES as readonly string[];
  const ordered = [preferred, ...base.filter((c) => c !== preferred)];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const code of ordered) {
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length >= PLACES_AUTOCOMPLETE_MAX_COUNTRIES) break;
  }
  return out;
}

const VALID_GEO_CODES = new Set<string>(BOOKING_GEO_COUNTRIES.map((c) => c.code));

export function isValidGeoCountry(code: string): code is BookingGeoCountryCode {
  return VALID_GEO_CODES.has(code);
}

/** Normalise un code ISO détecté (Netlify geo, etc.) vers un marché supporté. */
export function normalizeDetectedCountry(
  raw: string | null | undefined
): BookingGeoCountryCode | null {
  if (!raw) return null;
  const code = raw.trim().toLowerCase();
  const mapped = code === 'uk' ? 'gb' : code;
  return isValidGeoCountry(mapped) ? mapped : null;
}
